// pages/SAPProcessMapTickets.jsx — "Solman tickets" section of the SAP Process Map.
// Every Solman ticket is classified in the backend (sapProcmapTickets.js) into a kind
// (process issue / change & report request / master-data request / user & access), a module,
// the master-data object it concerns and the process step(s) it belongs to. This view shows the
// whole landscape at once so nothing is lost: master-data requests per object, issues per
// process step, and issues that could not be linked to any step (per module).
import { useEffect, useMemo, useState } from 'react';
import { api } from '../context/AuthContext';

const OBJ = {
  material:  ['Material master', 'Master material'],
  bom:       ['BOM, routing & work center', 'BOM, routing & work center'],
  price:     ['Prices, conditions & shipment-cost rates', 'Harga, kondisi & tarif shipment cost'],
  bp:        ['Vendor, customer & bank data', 'Data vendor, customer & bank'],
  fico:      ['G/L, cost center & asset', 'G/L, cost center & aset'],
  pm:        ['Equipment, functional location & plans', 'Equipment, functional location & plan'],
  qm:        ['Inspection characteristics & plans', 'Karakteristik & plan inspeksi'],
  transport: ['Vessels, routes & transporters', 'Kapal, rute & transporter'],
  '':        ['Other master data', 'Master data lainnya'],
};
const KIND = {
  issue:      ['Process issue', 'Masalah proses', '#dc2626'],
  change:     ['Change / report request', 'Permintaan perubahan / report', '#7c3aed'],
  masterdata: ['Master-data request', 'Permintaan master data', '#0284c7'],
  access:     ['User & access', 'User & akses', '#64748b'],
};
const ym = n => n ? String(n).slice(0, 4) + '-' + String(n).slice(4) : '';

export default function SAPProcessMapTickets({ landKey, land, lang, tr, initialFilter, onOpenStep }) {
  const id = lang === 'id';
  const L = (en, idn) => (id ? idn : en);
  const [sum, setSum] = useState(null);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState(initialFilter || null);   // { kind, obj?, module? }
  const [list, setList] = useState(null);
  const [q, setQ] = useState('');
  const [qApplied, setQApplied] = useState('');
  const [offset, setOffset] = useState(0);
  const [open, setOpen] = useState(null);
  const [att, setAtt] = useState(null);

  useEffect(() => { if (initialFilter) setFilter(initialFilter); }, [initialFilter]);

  // step id → { flowKey, name }
  const stepInfo = useMemo(() => {
    const m = {};
    Object.entries(land.flows || {}).forEach(([fk, f]) => (f.steps || []).forEach(s => { if (!m[s.id]) m[s.id] = { flowKey: fk, name: tr(s.t), flow: tr(f.name || '') }; }));
    return m;
  }, [land, tr]);

  useEffect(() => {
    let live = true; setSum(null); setErr(null);
    api.get('/api/sap-procmap/tickets/summary', { params: { land: landKey } })
      .then(r => { if (live) setSum(r.data); }).catch(e => { if (live) setErr(e?.response?.data?.error || e.message); });
    api.get('/api/sap-procmap/tickets/attach-job').then(r => { if (live) setAtt(r.data); }).catch(() => {});
    return () => { live = false; };
  }, [landKey]);

  useEffect(() => { setOffset(0); setOpen(null); }, [filter, qApplied, landKey]);
  useEffect(() => {
    if (!filter) { setList(null); return; }
    let live = true; setList(null);
    api.get('/api/sap-procmap/tickets', { params: { land: landKey, ...filter, q: qApplied || undefined, limit: 40, offset } })
      .then(r => { if (live) setList(r.data); }).catch(e => { if (live) setList({ error: e?.response?.data?.error || e.message, tickets: [] }); });
    return () => { live = false; };
  }, [landKey, filter, qApplied, offset]);

  if (err) return <p className="text-[13px] text-red-600">{err}</p>;
  if (!sum) return <p className="text-[13px] text-slate-400">{L('Loading…', 'Memuat…')}</p>;
  if (sum.note) return <div className="bg-white border border-slate-200 rounded-xl px-5 py-4 text-[13px] text-slate-500">{L('This landscape has no Solman tickets (KL is not on Solman).', 'Landscape ini tidak punya tiket Solman (KL tidak memakai Solman).')}</div>;

  const kindN = k => (sum.kinds.find(x => x.kind === k) || {}).n || 0;
  const pct = n => sum.total ? Math.round(100 * n / sum.total) + '%' : '';
  const mods = [...new Set(sum.modules.map(m => m.module))].sort((a, b) => sum.modules.filter(x => x.module === b).reduce((s, x) => s + x.n, 0) - sum.modules.filter(x => x.module === a).reduce((s, x) => s + x.n, 0));
  const modN = (m, k) => (sum.modules.find(x => x.module === m && x.kind === k) || {}).n || 0;
  const isOn = f => filter && Object.keys(f).every(k => filter[k] === f[k]) && Object.keys(filter).length === Object.keys(f).length;
  const cell = (f, n, title) => (
    <button type="button" disabled={!n} onClick={() => setFilter(f)} title={title}
      className={`w-full text-right px-2 py-1 rounded-md tabular-nums ${isOn(f) ? 'bg-brand-600 text-white' : n ? 'hover:bg-slate-100 text-slate-700' : 'text-slate-300'}`}>{n || '·'}</button>
  );
  const filterLabel = !filter ? '' : filter.kind === 'unlinked'
    ? L('Issues not linked to a process step', 'Masalah yang belum terhubung ke langkah proses') + (filter.module ? ' · ' + filter.module : '')
    : (KIND[filter.kind] ? KIND[filter.kind][id ? 1 : 0] : '') + (filter.obj !== undefined ? ' · ' + (OBJ[filter.obj] || OBJ[''])[id ? 1 : 0] : '') + (filter.module ? ' · ' + filter.module : '');

  return (
    <div className="flex flex-col gap-4">
      <div className="bg-white border border-slate-200 rounded-xl px-5 py-4">
        <div className="text-[14px] font-bold text-slate-800">{L('Solman tickets by type, module and process', 'Tiket Solman per jenis, modul dan proses')}</div>
        <p className="text-[12.5px] text-slate-500 mt-1 mb-0 max-w-4xl">
          {L(`${sum.total.toLocaleString()} tickets, each classified once: what kind of request it is, which module it belongs to, which master-data object it concerns and which process step it fits. A process step only lists real process issues of a matching module; master-data requests are counted here instead.`,
             `${sum.total.toLocaleString('id-ID')} tiket, masing-masing diklasifikasi sekali: jenis permintaannya, modulnya, objek master data yang diminta, dan langkah proses yang cocok. Langkah proses hanya menampilkan masalah proses dari modul yang sesuai; permintaan master data dihitung di sini.`)}
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
          {Object.entries(KIND).map(([k, [en, idn, col]]) => (
            <button key={k} type="button" onClick={() => setFilter({ kind: k })}
              className={`text-left rounded-lg border px-3 py-2 ${isOn({ kind: k }) ? 'ring-2 ring-brand-500' : ''}`} style={{ borderColor: col + '55', background: col + '0d' }}>
              <div className="text-[20px] font-bold tabular-nums" style={{ color: col }}>{kindN(k).toLocaleString()}</div>
              <div className="text-[12px] font-semibold text-slate-700">{id ? idn : en}</div>
              <div className="text-[11px] text-slate-400">{pct(kindN(k))}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="bg-white border border-slate-200 rounded-xl px-5 py-4 min-w-0">
          <div className="text-[13px] font-bold text-slate-800">{L('Master-data requests per object', 'Permintaan master data per objek')}</div>
          <div className="mt-2 divide-y divide-slate-100">
            {sum.objs.map(o => (
              <button key={o.obj || '-'} type="button" onClick={() => setFilter({ kind: 'masterdata', obj: o.obj || '' })}
                className={`w-full flex items-baseline gap-2 py-1.5 px-1 text-left rounded ${isOn({ kind: 'masterdata', obj: o.obj || '' }) ? 'bg-sky-50' : 'hover:bg-slate-50'}`}>
                <span className="text-[12.5px] text-slate-700">{(OBJ[o.obj || ''] || OBJ[''])[id ? 1 : 0]}</span>
                <span className="ml-auto text-[11px] text-slate-400">{L('last', 'terakhir')} {ym(o.last)}</span>
                <span className="w-14 text-right text-[12.5px] font-semibold tabular-nums text-slate-700">{o.n.toLocaleString()}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl px-5 py-4 min-w-0">
          <div className="text-[13px] font-bold text-slate-800">{L('Process issues & changes per step', 'Masalah proses & perubahan per langkah')}</div>
          <div className="mt-2 divide-y divide-slate-100 max-h-[340px] overflow-y-auto">
            {sum.steps.filter(s => stepInfo[s.step]).map(s => (
              <button key={s.step} type="button" onClick={() => onOpenStep && onOpenStep(stepInfo[s.step].flowKey, s.step)}
                className="w-full flex items-baseline gap-2 py-1.5 px-1 text-left rounded hover:bg-slate-50" title={L('Open this step in the process flow', 'Buka langkah ini di alur proses')}>
                <span className="text-[12.5px] text-slate-700">{stepInfo[s.step].name}</span>
                <span className="text-[11px] text-slate-400 truncate">{stepInfo[s.step].flow}</span>
                <span className="ml-auto text-[11px] text-slate-400 whitespace-nowrap">{L('last', 'terakhir')} {ym(s.last)}</span>
                <span className="w-10 text-right text-[12.5px] font-semibold tabular-nums text-slate-700">{s.n}</span>
              </button>
            ))}
          </div>
          <div className="text-[11px] text-slate-400 mt-2">{L('A ticket can fit up to three steps. Click a step to open it in the process flow.', 'Satu tiket bisa cocok ke maksimal tiga langkah. Klik langkah untuk membukanya di alur proses.')}</div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl px-5 py-4 overflow-x-auto">
        <div className="text-[13px] font-bold text-slate-800">{L('Per module', 'Per modul')}</div>
        <div className="text-[11.5px] text-slate-400">{L('Module = Solman component, corrected when the t-codes in the ticket clearly point to another module. Click a number to list the tickets.', 'Modul = komponen Solman, dikoreksi bila t-code di tiket jelas menunjuk modul lain. Klik angka untuk melihat tiketnya.')}</div>
        <table className="mt-2 text-[12.5px] min-w-[560px] w-full">
          <thead><tr className="text-[11px] text-slate-500">
            <th className="text-left font-semibold py-1">{L('Module', 'Modul')}</th>
            {Object.entries(KIND).map(([k, v]) => <th key={k} className="text-right font-semibold px-2">{v[id ? 1 : 0]}</th>)}
            <th className="text-right font-semibold px-2">{L('Issues without step', 'Masalah tanpa langkah')}</th>
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {mods.map(m => (
              <tr key={m}>
                <td className="py-0.5 font-semibold text-slate-700">{m === '?' ? L('(unknown)', '(tidak diketahui)') : m}</td>
                {Object.keys(KIND).map(k => <td key={k} className="px-1">{cell({ kind: k, module: m }, modN(m, k))}</td>)}
                <td className="px-1">{cell({ kind: 'unlinked', module: m }, (sum.unlinked.find(x => x.module === m) || {}).n || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filter && (
        <div className="bg-white border border-slate-200 rounded-xl px-5 py-4">
          <div className="flex flex-wrap items-baseline gap-2">
            <div className="text-[13.5px] font-bold text-slate-800">{filterLabel}</div>
            <div className="text-[12px] text-slate-500">{list && !list.error ? L(`${list.total} tickets`, `${list.total} tiket`) : ''}</div>
            <form className="ml-auto flex gap-1.5 w-full sm:w-auto" onSubmit={e => { e.preventDefault(); setQApplied(q.trim()); }}>
              <input value={q} onChange={e => setQ(e.target.value)} placeholder={L('Search ticket, text, number…', 'Cari tiket, teks, nomor…')}
                className="border border-slate-200 rounded-lg px-2.5 py-1 text-[12.5px] flex-1 sm:w-60" />
              <button type="submit" className="rounded-lg border border-slate-200 px-2.5 text-[12px] font-semibold text-slate-600 hover:bg-slate-50">{L('Search', 'Cari')}</button>
              <button type="button" onClick={() => setFilter(null)} className="rounded-lg border border-slate-200 px-2.5 text-[12px] text-slate-500 hover:bg-slate-50">✕</button>
            </form>
          </div>
          {!list && <p className="text-[12.5px] text-slate-400 mt-2">{L('Loading…', 'Memuat…')}</p>}
          {list && list.error && <p className="text-[12.5px] text-red-600 mt-2">{list.error}</p>}
          {list && !list.error && !list.tickets.length && <p className="text-[12.5px] text-slate-500 mt-2">{L('No tickets.', 'Tidak ada tiket.')}</p>}
          <div className="mt-2 divide-y divide-slate-100">
            {(list?.tickets || []).map(t => <TicketRow key={t.ticket_id} t={t} id={id} open={open === t.ticket_id} onToggle={() => setOpen(open === t.ticket_id ? null : t.ticket_id)}
              stepName={t.top_step && stepInfo[t.top_step] ? stepInfo[t.top_step].name : ''} />)}
          </div>
          {list && !list.error && list.total > 40 && (
            <div className="flex items-center gap-2 mt-3 text-[12px] text-slate-500">
              <button type="button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 40))} className="rounded border border-slate-200 px-2 py-0.5 disabled:opacity-40">‹</button>
              <span>{offset + 1}–{Math.min(offset + 40, list.total)} / {list.total}</span>
              <button type="button" disabled={offset + 40 >= list.total} onClick={() => setOffset(offset + 40)} className="rounded border border-slate-200 px-2 py-0.5 disabled:opacity-40">›</button>
            </div>
          )}
        </div>
      )}
      <div className="text-[11px] text-slate-400">{L('Classified', 'Diklasifikasi')} {sum.classified_at ? sum.classified_at + ' UTC' : '-'} · {L('rules v', 'aturan v')}{sum.rules_version} · {L('new tickets are added automatically', 'tiket baru ditambahkan otomatis')}
        {att && (() => { const n = k => (att.stored.find(x => x.status === k) || {}).n || 0; const chk = att.stored.reduce((a, x) => a + x.n, 0);
          return <> · {L(`attachments read for ${chk} unlinked tickets (${n('done')} with text, ${n('none')} without attachments)`, `lampiran dibaca untuk ${chk} tiket tanpa langkah (${n('done')} berisi teks, ${n('none')} tanpa lampiran)`)}
            {att.job.running ? L(` · reading now: ${att.job.done}/${att.job.target}`, ` · sedang dibaca: ${att.job.done}/${att.job.target}`) : ''}
            {!att.job.running && att.not_yet_checked ? L(` · ${att.not_yet_checked} not read yet`, ` · ${att.not_yet_checked} belum dibaca`) : ''}</>; })()}</div>
    </div>
  );
}

export function TicketRow({ t, id, open, onToggle, stepName, color = '#475569' }) {
  const replies = (t.texts || []).filter(x => x.type !== 'Description');
  const desc = (t.texts || []).find(x => x.type === 'Description');
  const k = KIND[t.kind];
  return (
    <div className="py-2">
      <button type="button" onClick={onToggle} className="w-full text-left flex flex-wrap items-baseline gap-x-2">
        <span className="font-mono text-[11.5px] text-slate-400">{t.ticket_id}</span>
        <span className="text-[13px] font-semibold text-slate-700">{t.description}</span>
        <span className="text-[11.5px] text-slate-400">{t.company} · {t.created_on} · {t.module || t.sap_component || '-'}{t.sap_component && t.module && !String(t.sap_component).startsWith(t.module) ? ` (Solman: ${t.sap_component})` : ''} · {t.status}</span>
        {k && <span className="text-[10.5px] font-semibold rounded px-1.5" style={{ color: k[2], background: k[2] + '14' }}>{k[id ? 1 : 0]}</span>}
        {stepName && <span className="text-[10.5px] rounded px-1.5 bg-slate-100 text-slate-600">{stepName}</span>}
        {(t.via === 'attach' || t.via === 'reply') && (
          <span className="text-[10.5px] rounded px-1.5 bg-amber-50 text-amber-700" title={id ? 'Langkah proses dikenali dari isi lampiran atau jawaban tim, bukan dari judul tiket' : 'Process step recognised from the attachment or the team reply, not from the ticket title'}>
            {t.via === 'attach' ? (id ? 'cocok lewat lampiran' : 'matched via attachment') : (id ? 'cocok lewat jawaban' : 'matched via reply')}
          </span>
        )}
        {t.attach_names && <span className="text-[10.5px] text-slate-400 truncate max-w-[260px]" title={t.attach_names}>📎 {t.attach_names}</span>}
        <span className="ml-auto text-[11.5px]" style={{ color }}>{replies.length ? (id ? `${replies.length} jawaban` : `${replies.length} replies`) : (id ? 'tanpa jawaban' : 'no replies')} {open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="mt-2 grid gap-2">
          {desc && <div className="bg-slate-50 rounded-lg p-2.5 text-[12.5px] text-slate-600 whitespace-pre-wrap"><b>{id ? 'Permintaan' : 'Request'}</b> · {desc.by}<br />{desc.text}</div>}
          {replies.map((x, i) => (
            <div key={i} className="rounded-lg p-2.5 text-[12.5px] text-slate-700 whitespace-pre-wrap" style={{ background: color + '12' }}>
              <b>{x.type === 'Solution' ? (id ? 'Solusi' : 'Solution') : (id ? 'Jawaban' : 'Reply')}</b> · {x.by} · {x.on}<br />{x.text}
            </div>
          ))}
          {!(t.texts || []).length && <p className="text-[12px] text-slate-400 m-0">{id ? 'Teks tiket belum tersinkron.' : 'Ticket text not synced yet.'} {t.processor ? `· ${t.processor}` : ''}</p>}
        </div>
      )}
    </div>
  );
}

export const TICKET_OBJ_LABEL = OBJ;
